import { FC, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { rankingsService, type RankingUser, type RankingSector } from '@/services/rankings.service';
import InfoTooltip from '@/components/InfoTooltip';
import RankingTour from '@/components/tour/RankingTour';

const RankingPage: FC = () => {
  const navigate = useNavigate();
  const [showFullRanking, setShowFullRanking] = useState(false);
  const [rankingType, setRankingType] = useState<'sector' | 'person'>('sector');
  const [selectedSector, setSelectedSector] = useState<string>('all');
  const [, setIsLoading] = useState(true);
  const [users, setUsers] = useState<RankingUser[]>([]);
  const [sectors, setSectors] = useState<RankingSector[]>([]);
  const [, setMyPosition] = useState<number | null>(null);
  // Distinct sector names for filter dropdown (from initial unfiltered fetch)
  const [sectorOptions, setSectorOptions] = useState<string[]>([]);

  // Fetch initial data
  useEffect(() => {
    const fetchRankings = async () => {
      try {
        setIsLoading(true);
        const [usersData, sectorsData, positionData] = await Promise.all([
          rankingsService.getUserRankings(100),
          rankingsService.getSectorRankings(100),
          rankingsService.getMyPosition('week'),
        ]);
        setUsers(usersData);
        setSectors(sectorsData);
        setMyPosition(positionData.position);
        // Extract unique sector names for dropdown
        setSectorOptions(['all', ...new Set(usersData.map(u => u.sector).filter(Boolean) as string[])]);
      } catch (error) {
        console.error('Erro ao carregar rankings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRankings();
  }, []);

  // When sector filter changes, re-fetch from API with sector_id
  const handleSectorChange = useCallback(async (sectorName: string) => {
    setSelectedSector(sectorName);
    setShowFullRanking(false);

    if (sectorName === 'all') {
      // Re-fetch unfiltered
      try {
        const data = await rankingsService.getUserRankings(100);
        setUsers(data);
      } catch { /* keep existing */ }
    } else {
      // Find sector_id from sectors list
      const sector = sectors.find(s => s.name === sectorName);
      if (sector) {
        try {
          const data = await rankingsService.getUserRankings(100, sector.id);
          setUsers(data);
        } catch { /* keep existing */ }
      }
    }
  }, [sectors]);

  // Derive podium (top 3)
  const podium = sectors.slice(0, 3);
  const personPodium = users.slice(0, 3);

  const displayedSectors = showFullRanking ? sectors : sectors.slice(0, 4);
  const displayedPersons = showFullRanking ? users : users.slice(0, 4);

  const getTrendIcon = (xpChange: number) => {
    if (xpChange > 0) {
      return <span className="material-symbols-outlined text-emerald-400">trending_up</span>;
    } else if (xpChange < 0) {
      return <span className="material-symbols-outlined text-rose-500">trending_down</span>;
    } else {
      return <span className="material-symbols-outlined text-content-muted">trending_flat</span>;
    }
  };

  const getPodiumHeight = (position: number) => {
    switch (position) {
      case 1:
        return 'h-60';
      case 2:
        return 'h-52';
      case 3:
        return 'h-[170px]';
      default:
        return '';
    }
  };

  return (
    <>
      {/* Header */}
      <header className="h-20 border-b border-theme-border flex items-center justify-between pl-16 pr-4 md:px-8 sticky top-0 bg-surface-primary/80 backdrop-blur-md z-10">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-content-primary">Ranking</h2>
            <span className="bg-accent/20 text-accent text-2xs font-bold px-3 py-1 rounded-full uppercase tracking-wider">
              Temporada Atual
            </span>
          </div>
        </header>

        <div className="p-4 md:p-8 max-w-6xl mx-auto w-full space-y-12">
          {/* Tabs and Filters */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 justify-between">
            <div
              className="flex items-center gap-2 bg-surface-secondary p-1 rounded-xl"
              data-tour="ranking-toggle"
            >
              <button
                onClick={() => { setRankingType('sector'); setShowFullRanking(false); }}
                className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all ${
                  rankingType === 'sector'
                    ? 'bg-accent text-accent-contrast'
                    : 'text-content-secondary hover:text-content-primary'
                }`}
                aria-label="Ver ranking por setor"
              >
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-lg">corporate_fare</span>
                  Por Setor
                </span>
              </button>
              <button
                onClick={() => { setRankingType('person'); setShowFullRanking(false); }}
                className={`px-6 py-2.5 rounded-lg font-bold text-sm transition-all ${
                  rankingType === 'person'
                    ? 'bg-accent text-accent-contrast'
                    : 'text-content-secondary hover:text-content-primary'
                }`}
                aria-label="Ver ranking geral"
              >
                <span className="flex items-center gap-2">
                  <span className="material-symbols-outlined text-lg">person</span>
                  Ranking Geral
                </span>
              </button>
            </div>

            {/* Sector Filter - Only visible when viewing persons */}
            {rankingType === 'person' && (
              <div className="flex items-center gap-3">
                <label htmlFor="sector-filter" className="text-sm text-content-secondary font-medium">
                  Filtrar por setor:
                </label>
                <select
                  id="sector-filter"
                  value={selectedSector}
                  onChange={(e) => handleSectorChange(e.target.value)}
                  className="bg-surface-secondary border border-theme-border text-content-primary px-4 py-2 rounded-xl text-sm font-medium focus:outline-none focus:border-accent transition-colors"
                  aria-label="Selecionar setor"
                >
                  {sectorOptions.map(sector => (
                    <option key={sector} value={sector}>
                      {sector === 'all' ? 'Todos os Setores' : sector}
                    </option>
                  ))}
                </select>
                {selectedSector !== 'all' && users.length > 0 && (
                  <span className="text-xs text-content-muted">
                    ({users.length} pessoa{users.length !== 1 ? 's' : ''})
                  </span>
                )}
              </div>
            )}
          </div>

          {/* Podium Section */}
          <section data-tour="ranking-podium">
            <div className="flex items-end justify-center gap-2 sm:gap-4 md:gap-8 pt-10 pb-4">
              {rankingType === 'sector' ? (
                <>
                  {/* Sector Podium - 2nd Place */}
                  {podium[1] && (
                  <div className="flex flex-col items-center w-24 sm:w-40 md:w-48 group">
                    <div className="mb-4 relative">
                      <div className="w-16 h-16 rounded-2xl bg-slate-700 border-2 border-slate-500 overflow-hidden transform group-hover:-translate-y-1 transition-transform flex items-center justify-center">
                        <span className="material-symbols-outlined text-2xl text-content-muted">corporate_fare</span>
                      </div>
                      <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-slate-400 text-slate-900 flex items-center justify-center font-bold border-4 border-surface-primary">
                        2
                      </div>
                    </div>
                    <div
                      className={`${getPodiumHeight(2)} w-full rounded-t-2xl flex flex-col items-center justify-center border border-theme-border border-b-0 p-4 bg-gradient-to-t from-transparent to-white/5`}
                    >
                      <span className="text-content-primary font-bold text-center text-sm mb-1">
                        {podium[1].name}
                      </span>
                      <span className="text-content-muted text-xs mb-1">{podium[1].member_count} membros</span>
                      <span className="text-content-secondary text-xs font-medium">
                        {podium[1].total_xp.toLocaleString('pt-BR')} XP
                      </span>
                    </div>
                  </div>
                  )}

                  {/* Sector Podium - 1st Place */}
                  {podium[0] && (
                  <div className="flex flex-col items-center w-28 sm:w-48 md:w-56 group">
                    <div className="mb-4 relative">
                      <div className="w-24 h-24 rounded-2xl bg-accent/20 border-4 border-accent overflow-hidden shadow-lg shadow-accent/30 transform group-hover:-translate-y-2 transition-transform flex items-center justify-center">
                        <span className="material-symbols-outlined text-4xl text-accent">corporate_fare</span>
                      </div>
                      <div className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-accent text-accent-contrast flex items-center justify-center font-black border-4 border-surface-primary text-lg">
                        1
                      </div>
                      <div className="absolute -top-12 left-1/2 -translate-x-1/2">
                        <span className="material-symbols-outlined text-yellow-400 text-4xl animate-pulse">
                          workspace_premium
                        </span>
                      </div>
                    </div>
                    <div
                      className={`${getPodiumHeight(1)} w-full rounded-t-2xl flex flex-col items-center justify-center border border-accent/30 border-b-0 p-4 bg-gradient-to-t from-transparent to-accent/10`}
                    >
                      <span className="text-content-primary font-black text-center text-lg mb-1">
                        {podium[0].name}
                      </span>
                      <span className="text-content-muted text-xs mb-1">{podium[0].member_count} membros</span>
                      <span className="text-accent font-bold">
                        {podium[0].total_xp.toLocaleString('pt-BR')} XP
                      </span>
                      <div className="mt-4 px-3 py-1 rounded-full bg-accent/10 text-accent text-2xs font-bold uppercase">
                        Líderes Atuais
                      </div>
                    </div>
                  </div>
                  )}

                  {/* Sector Podium - 3rd Place */}
                  {podium[2] && (
                  <div className="flex flex-col items-center w-24 sm:w-40 md:w-48 group">
                    <div className="mb-4 relative">
                      <div className="w-16 h-16 rounded-2xl bg-slate-700 border-2 border-orange-800/50 overflow-hidden transform group-hover:-translate-y-1 transition-transform flex items-center justify-center">
                        <span className="material-symbols-outlined text-2xl text-content-muted">corporate_fare</span>
                      </div>
                      <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-orange-700 text-white flex items-center justify-center font-bold border-4 border-surface-primary">
                        3
                      </div>
                    </div>
                    <div
                      className={`${getPodiumHeight(3)} w-full rounded-t-2xl flex flex-col items-center justify-center border border-theme-border border-b-0 p-4 bg-gradient-to-t from-transparent to-white/5`}
                    >
                      <span className="text-content-primary font-bold text-center text-sm mb-1">
                        {podium[2].name}
                      </span>
                      <span className="text-content-muted text-xs mb-1">{podium[2].member_count} membros</span>
                      <span className="text-content-secondary text-xs font-medium">
                        {podium[2].total_xp.toLocaleString('pt-BR')} XP
                      </span>
                    </div>
                  </div>
                  )}
                </>
              ) : (
                personPodium.length > 0 && (
                  <>
                    {/* Person Podium - 2nd Place */}
                    {personPodium[1] && (
                    <div className="flex flex-col items-center w-24 sm:w-40 md:w-48 group cursor-pointer" onClick={() => navigate(`/perfil/${personPodium[1].id}`)}>
                      <div className="mb-4 relative">
                        <div className="w-16 h-16 rounded-full bg-slate-700 border-2 border-slate-500 overflow-hidden transform group-hover:-translate-y-1 transition-transform">
                          {personPodium[1].avatar ? (
                            <img alt={personPodium[1].name} className="w-full h-full object-cover" src={personPodium[1].avatar} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-accent/10">
                              <span className="material-symbols-outlined text-xl text-accent">person</span>
                            </div>
                          )}
                        </div>
                        <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-slate-400 text-slate-900 flex items-center justify-center font-bold border-4 border-surface-primary">
                          2
                        </div>
                      </div>
                      <div
                        className={`${getPodiumHeight(2)} w-full rounded-t-2xl flex flex-col items-center justify-center border border-theme-border border-b-0 p-4 bg-gradient-to-t from-transparent to-white/5`}
                      >
                        <span className="text-content-primary font-bold text-center text-sm mb-1 truncate max-w-full">
                          {personPodium[1].name}
                        </span>
                        <span className="text-accent-cyan text-xs font-medium mb-1">
                          {personPodium[1].sector || ''}
                        </span>
                        <span className="text-content-secondary text-xs font-medium">
                          {personPodium[1].total_xp.toLocaleString('pt-BR')} XP
                        </span>
                      </div>
                    </div>
                    )}

                    {/* Person Podium - 1st Place */}
                    {personPodium[0] && (
                    <div className="flex flex-col items-center w-28 sm:w-48 md:w-56 group cursor-pointer" onClick={() => navigate(`/perfil/${personPodium[0].id}`)}>
                      <div className="mb-4 relative">
                        <div className="w-24 h-24 rounded-full bg-accent/20 border-4 border-accent overflow-hidden shadow-lg shadow-accent/30 transform group-hover:-translate-y-2 transition-transform">
                          {personPodium[0].avatar ? (
                            <img alt={personPodium[0].name} className="w-full h-full object-cover" src={personPodium[0].avatar} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-accent/10">
                              <span className="material-symbols-outlined text-2xl text-accent">person</span>
                            </div>
                          )}
                        </div>
                        <div className="absolute -top-4 -right-4 w-10 h-10 rounded-full bg-accent text-accent-contrast flex items-center justify-center font-black border-4 border-surface-primary text-lg">
                          1
                        </div>
                        <div className="absolute -top-12 left-1/2 -translate-x-1/2">
                          <span className="material-symbols-outlined text-yellow-400 text-4xl animate-pulse">
                            workspace_premium
                          </span>
                        </div>
                      </div>
                      <div
                        className={`${getPodiumHeight(1)} w-full rounded-t-2xl flex flex-col items-center justify-center border border-accent/30 border-b-0 p-4 bg-gradient-to-t from-transparent to-accent/10`}
                      >
                        <span className="text-content-primary font-black text-center text-lg mb-1 truncate max-w-full">
                          {personPodium[0].name}
                        </span>
                        <span className="text-accent-cyan text-sm font-medium mb-2">
                          {personPodium[0].sector || ''}
                        </span>
                        <span className="text-accent font-bold">
                          {personPodium[0].total_xp.toLocaleString('pt-BR')} XP
                        </span>
                        <div className="mt-4 px-3 py-1 rounded-full bg-accent/10 text-accent text-2xs font-bold uppercase">
                          Líder Atual
                        </div>
                      </div>
                    </div>
                    )}

                    {/* Person Podium - 3rd Place */}
                    {personPodium[2] && (
                    <div className="flex flex-col items-center w-24 sm:w-40 md:w-48 group cursor-pointer" onClick={() => navigate(`/perfil/${personPodium[2].id}`)}>
                      <div className="mb-4 relative">
                        <div className="w-16 h-16 rounded-full bg-slate-700 border-2 border-orange-800/50 overflow-hidden transform group-hover:-translate-y-1 transition-transform">
                          {personPodium[2].avatar ? (
                            <img alt={personPodium[2].name} className="w-full h-full object-cover" src={personPodium[2].avatar} />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center bg-accent/10">
                              <span className="material-symbols-outlined text-xl text-accent">person</span>
                            </div>
                          )}
                        </div>
                        <div className="absolute -top-3 -right-3 w-8 h-8 rounded-full bg-orange-700 text-white flex items-center justify-center font-bold border-4 border-surface-primary">
                          3
                        </div>
                      </div>
                      <div
                        className={`${getPodiumHeight(3)} w-full rounded-t-2xl flex flex-col items-center justify-center border border-theme-border border-b-0 p-4 bg-gradient-to-t from-transparent to-white/5`}
                      >
                        <span className="text-content-primary font-bold text-center text-sm mb-1 truncate max-w-full">
                          {personPodium[2].name}
                        </span>
                        <span className="text-accent-cyan text-xs font-medium mb-1">
                          {personPodium[2].sector || ''}
                        </span>
                        <span className="text-content-secondary text-xs font-medium">
                          {personPodium[2].total_xp.toLocaleString('pt-BR')} XP
                        </span>
                      </div>
                    </div>
                    )}
                  </>
                )
              )}
            </div>
            <div className="h-[1px] w-full bg-theme-border"></div>
          </section>

          {/* Ranking List */}
          <section className="space-y-4" data-tour="ranking-table">
            {rankingType === 'sector' ? (
              <>
                <div className="hidden md:flex items-center justify-between px-6 text-content-muted text-xs font-bold uppercase tracking-widest">
                  <div className="flex items-center gap-12">
                    <span className="w-8">Pos</span>
                    <span className="w-48">Setor</span>
                  </div>
                  <div className="flex items-center gap-16">
                    <span className="w-24 text-center">Membros</span>
                    <span className="w-24 inline-flex items-center justify-center gap-1">
                      Tendência
                      <InfoTooltip termKey="tendencia" iconOnly />
                    </span>
                    <span className="w-24 text-right">Total XP</span>
                  </div>
                </div>

                <div className="space-y-3">
                  {displayedSectors.map((sector) => (
                    <div
                      key={sector.id}
                      className="bg-surface-secondary border rounded-xl p-4 md:p-5 transition-all hover:bg-surface-hover border-theme-border"
                    >
                      {/* Mobile: card empilhado */}
                      <div className="flex md:hidden flex-col gap-3">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-content-secondary text-lg shrink-0">
                            {String(sector.position).padStart(2, '0')}
                          </span>
                          <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-800 border border-theme-border flex items-center justify-center shrink-0">
                            <span className="material-symbols-outlined text-lg text-content-muted">corporate_fare</span>
                          </div>
                          <div className="flex-1 min-w-0">
                            <h4 className="text-content-primary font-bold truncate">{sector.name}</h4>
                            <p className="text-content-muted text-xs">Média: {sector.avg_xp_per_member.toLocaleString('pt-BR')} XP</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 pt-2 border-t border-theme-border">
                          <div className="text-center">
                            <p className="text-content-muted text-2xs uppercase tracking-wider">Membros</p>
                            <p className="text-content-secondary font-bold">{sector.member_count}</p>
                          </div>
                          <div className="text-center">
                            <p className="text-content-muted text-2xs uppercase tracking-wider">Tendência</p>
                            <div className="flex justify-center">{getTrendIcon(sector.xp_change)}</div>
                          </div>
                          <div className="text-center">
                            <p className="text-content-muted text-2xs uppercase tracking-wider">Total XP</p>
                            <p className="text-content-primary font-bold">{sector.total_xp.toLocaleString('pt-BR')}</p>
                          </div>
                        </div>
                      </div>

                      {/* Desktop: linha horizontal */}
                      <div className="hidden md:flex items-center justify-between">
                        <div className="flex items-center gap-12">
                          <span className="w-8 font-bold text-content-secondary">
                            {String(sector.position).padStart(2, '0')}
                          </span>
                          <div className="flex items-center gap-4 w-48">
                            <div className="w-10 h-10 rounded-lg overflow-hidden bg-slate-800 border border-theme-border flex items-center justify-center">
                              <span className="material-symbols-outlined text-lg text-content-muted">corporate_fare</span>
                            </div>
                            <div>
                              <h4 className="text-content-primary font-bold flex items-center gap-2">
                                {sector.name}
                              </h4>
                              <p className="text-content-muted text-xs">Média: {sector.avg_xp_per_member.toLocaleString('pt-BR')} XP</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-16">
                          <div className="w-24 flex justify-center">
                            <span className="text-content-secondary text-sm font-medium">{sector.member_count}</span>
                          </div>
                          <div className="w-24 flex justify-center">{getTrendIcon(sector.xp_change)}</div>
                          <span className="w-24 text-right font-bold text-content-primary">
                            {sector.total_xp.toLocaleString('pt-BR')} XP
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {!showFullRanking && sectors.length > 4 && (
                  <div className="flex justify-center mt-8">
                    <button
                      onClick={() => setShowFullRanking(true)}
                      className="flex items-center gap-2 text-accent font-bold hover:gap-3 transition-all"
                    >
                      Ver ranking completo
                      <span className="material-symbols-outlined">keyboard_double_arrow_down</span>
                    </button>
                  </div>
                )}
              </>
            ) : (
              users.length > 0 && (
                <>
                  <div className="hidden md:flex items-center justify-between px-6 text-content-muted text-xs font-bold uppercase tracking-widest">
                    <div className="flex items-center gap-12">
                      <span className="w-8">Pos</span>
                      <span className="w-48">Pessoa</span>
                    </div>
                    <div className="flex items-center gap-16">
                      <span className="w-32 text-center">Setor</span>
                      <span className="w-24 inline-flex items-center justify-center gap-1">
                      Tendência
                      <InfoTooltip termKey="tendencia" iconOnly />
                    </span>
                      <span className="w-24 text-right">Total XP</span>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {displayedPersons.map((person) => (
                      <div
                        key={person.id}
                        role="button"
                        tabIndex={0}
                        data-testid="ranking-row"
                        onClick={() => navigate(`/perfil/${person.id}`)}
                        onKeyDown={(e) => { if (e.key === 'Enter') navigate(`/perfil/${person.id}`); }}
                        className="bg-surface-secondary border rounded-xl p-4 md:p-5 transition-all hover:bg-surface-hover border-theme-border cursor-pointer"
                      >
                        {/* Mobile: card empilhado */}
                        <div className="flex md:hidden flex-col gap-3">
                          <div className="flex items-center gap-3">
                            <span className="font-bold text-content-secondary text-lg shrink-0">
                              {String(person.position).padStart(2, '0')}
                            </span>
                            <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-800 border border-theme-border shrink-0">
                              {person.avatar ? (
                                <img alt={person.name} className="w-full h-full object-cover" src={person.avatar} />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center bg-accent/10">
                                  <span className="material-symbols-outlined text-lg text-accent">person</span>
                                </div>
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="text-content-primary font-bold truncate">{person.name}</h4>
                              <p className="text-content-muted text-xs truncate">Nível {person.level}</p>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2 pt-2 border-t border-theme-border">
                            <div className="text-center min-w-0">
                              <p className="text-content-muted text-2xs uppercase tracking-wider">Setor</p>
                              <p className="text-accent-cyan text-xs font-medium truncate">{person.sector || 'Sem setor'}</p>
                            </div>
                            <div className="text-center">
                              <p className="text-content-muted text-2xs uppercase tracking-wider">Tendência</p>
                              <div className="flex justify-center">{getTrendIcon(person.xp_change)}</div>
                            </div>
                            <div className="text-center">
                              <p className="text-content-muted text-2xs uppercase tracking-wider">Total XP</p>
                              <p className="text-content-primary font-bold">{person.total_xp.toLocaleString('pt-BR')}</p>
                            </div>
                          </div>
                        </div>

                        {/* Desktop: linha horizontal */}
                        <div className="hidden md:flex items-center justify-between">
                          <div className="flex items-center gap-12">
                            <span className="w-8 font-bold text-content-secondary">
                              {String(person.position).padStart(2, '0')}
                            </span>
                            <div className="flex items-center gap-4 w-48">
                              <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-800 border border-theme-border shrink-0">
                                {person.avatar ? (
                                  <img alt={person.name} className="w-full h-full object-cover" src={person.avatar} />
                                ) : (
                                  <div className="w-full h-full flex items-center justify-center bg-accent/10">
                                    <span className="material-symbols-outlined text-lg text-accent">person</span>
                                  </div>
                                )}
                              </div>
                              <div className="min-w-0">
                                <h4 className="text-content-primary font-bold truncate">
                                  {person.name}
                                </h4>
                                <p className="text-content-muted text-xs truncate">Nível {person.level}</p>
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-16">
                            <div className="w-32 flex justify-center">
                              <span className="text-accent-cyan text-xs font-medium px-3 py-1 rounded-full bg-accent-cyan/10 truncate max-w-full">
                                {person.sector || 'Sem setor'}
                              </span>
                            </div>
                            <div className="w-24 flex justify-center">{getTrendIcon(person.xp_change)}</div>
                            <span className="w-24 text-right font-bold text-content-primary">
                              {person.total_xp.toLocaleString('pt-BR')} XP
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {!showFullRanking && users.length > 4 && (
                    <div className="flex justify-center mt-8">
                      <button
                        onClick={() => setShowFullRanking(true)}
                        className="flex items-center gap-2 text-accent font-bold hover:gap-3 transition-all"
                      >
                        Ver ranking completo
                        <span className="material-symbols-outlined">keyboard_double_arrow_down</span>
                      </button>
                    </div>
                  )}
                </>
              )
            )}
          </section>
        </div>
        <RankingTour />
    </>
  );
};

export default RankingPage;
